import { User, Calendar, AlertTriangle, Link2 } from "lucide-react";
import { StatusBadge } from "@/components/common/StatusBadge";
import { launchBadge, priorityBadge, isOverdue, milestoneState, STATE_META } from "@/data/launchOptions";

export const MilestoneCard = ({ milestone: m, today, depIncomplete, onOpen, draggable, onDragStart }) => {
  const overdue = isOverdue(m, today);
  const state = milestoneState(m, today);
  const c = STATE_META[state].color;
  const pct = m.status === "Complete" ? 100 : Math.max(0, Math.min(100, Number(m.completion) || 0));

  return (
    <div
      draggable={draggable}
      onDragStart={onDragStart}
      onClick={onOpen}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen?.(); } }}
      role="button"
      tabIndex={0}
      aria-label={`View milestone: ${m.title}`}
      data-testid={`milestone-card-${m.id}`}
      className="group relative cursor-pointer overflow-hidden rounded-2xl border border-[#E4E9F3] bg-white p-4 pl-5 shadow-[0_2px_10px_rgba(20,36,95,0.05)] transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_8px_20px_rgba(20,36,95,0.10)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#E0A82E]/50"
    >
      <span className="absolute inset-y-0 left-0 w-1.5" style={{ backgroundColor: c }} />
      <div className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: c }} />
          <p className="truncate text-sm font-semibold leading-snug text-[#14245F]">{m.title}</p>
        </div>
        <StatusBadge status={priorityBadge(m.priority)} label={m.priority} />
      </div>
      <p className="mt-1 pl-4 text-xs font-medium text-[#5A6B8C]"><span className="font-semibold" style={{ color: (m.workstream && ({Product:"#1D4ED8",Content:"#0EA5A4",Commercial:"#E0A82E",Marketing:"#EC4899",Validation:"#8B5CF6",Launch:"#16A34A"}[m.workstream])) || "#5A6B8C" }}>{m.workstream || m.category}</span>{m.readiness_weight ? ` · wt ${m.readiness_weight}` : ""}</p>

      <div className="mt-3">
        <div className="mb-1 flex items-center justify-between text-[11px] font-semibold">
          <span style={{ color: c }}>{STATE_META[state].label}</span>
          <span className="text-[#5A6B8C]">{pct}%</span>
        </div>
        <div className="h-2 w-full overflow-hidden rounded-full bg-[#EEF2FB]">
          <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, backgroundColor: c }} />
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-[#5A6B8C]">
        <span className="inline-flex items-center gap-1"><User size={12} strokeWidth={2} /> {m.owner}</span>
        {m.due_date && <span className={`inline-flex items-center gap-1 ${overdue ? "font-bold text-[#DC2626]" : ""}`}><Calendar size={12} strokeWidth={2} /> {m.due_date}</span>}
      </div>
      {depIncomplete && (
        <div className="mt-2 flex items-center gap-1 rounded-lg bg-[#06B6D4]/10 px-2 py-1 text-[11px] font-medium text-[#0E7490]">
          <Link2 size={12} /> Depends on an incomplete milestone
        </div>
      )}
      {m.current_blocker && (
        <div className="mt-2 flex items-start gap-1 rounded-lg bg-[#FB6F5A]/[0.08] px-2 py-1 text-[11px] font-medium text-[#C2410C]">
          <AlertTriangle size={12} className="mt-0.5 shrink-0" /><span className="line-clamp-2">{m.current_blocker}</span>
        </div>
      )}
    </div>
  );
};
